// Schvalování propadlých tréninků (Log Expired YYYY).
// Editor Sheet "Log Expired YYYY" pro role owner+manager (CSM read-only).
// Default měsíc = předchozí (= ten který cron právě hodnotí).
//
// Sloupec Schváleno: ANO = klient propadne, NE = odpouštíme. Phase 2 cron 1.-3. den
// měsíce přepočítává Stav YYYY podle aktuálního stavu.

import React, { useEffect, useMemo, useState } from "react";
import ZalozkaLink from "../components/ZalozkaLink.jsx";
import {
  AlertTriangle,
  ArrowRight,
  Calculator,
  CheckCircle2,
  Clock,
  Filter,
  Info,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";
import Card from "../components/Card.jsx";
import Pill from "../components/Pill.jsx";
import InfoTip from "../components/InfoTip.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import ConfirmDialog, { isConfirmBypassed } from "../components/ConfirmDialog.jsx";
import ClientAutocomplete, { CLIENT_AUTOCOMPLETE_TOOLTIP } from "../components/ClientAutocomplete.jsx";
import { Stranka, Modal, Field, DlazdiceFiltr, EmptyState, SkeletonRows, Hlaska, AkceZnovu } from "../components/ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { useClientsList } from "../lib/clientsCache.js";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { fetchLogExpired, invalidateLogExpiredCache } from "../lib/logExpiredCache.js";

const MONTH_NAMES = [
  "Leden", "Únor", "Březen", "Duben", "Květen", "Červen",
  "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec",
];

// Default = predchozi mesic (mesic ktery cron prave hodnoti)
function getDefaultMonth() {
  const d = new Date();
  const m = d.getMonth(); // 0-based, takze 0 = leden
  // Predchozi mesic = m. Pro leden by byl prosinec predchoziho roku, ale to user vyresi rok dropdownem.
  return m === 0 ? 12 : m; // 1-based
}
function getDefaultYear() {
  const d = new Date();
  if (d.getMonth() === 0) return d.getFullYear() - 1;
  return d.getFullYear();
}


function StatusPill({ value }) {
  if (value === "ANO") return <Pill tone="red">Propadne</Pill>;
  if (value === "NE") return <Pill tone="green">Odpuštěno</Pill>;
  return <Pill tone="slate">—</Pill>;
}

// Stav přepočtu — kombinace Schváleno + Přičteno.
// Schváleno=NE → "Nepřičítá se" (klient propadnutí odpuštěno, automat ho ignoruje)
// Schváleno=ANO + Přičteno=prázdné → "Čeká" (čeká na nejbližší přepočet)
// Schváleno=ANO + Přičteno=ANO → "Přičteno" (klient už má propadlé tréninky započítané)
// Schváleno=NE + Přičteno=ANO → "Odečte se" (admin změnil ANO→NE po přičtení, příští přepočet odečte zpět)
function PricitenoPill({ schvaleno, priciteno }) {
  if (priciteno === "ANO" && schvaleno === "ANO") {
    return <Pill tone="amber">Přičteno</Pill>;
  }
  if (priciteno === "ANO" && schvaleno === "NE") {
    return <Pill tone="purple">Odečte se</Pill>;
  }
  if (schvaleno === "NE") {
    return <Pill tone="slate">Nepřičítá se</Pill>;
  }
  if (schvaleno === "ANO") {
    return <Pill tone="blue">Čeká</Pill>;
  }
  return <Pill tone="slate">—</Pill>;
}

function PilotPill({ value }) {
  if (!value) return null;
  if (value.toLowerCase() === "automatizace") return <Pill tone="blue">automat</Pill>;
  return <Pill tone="purple">{value}</Pill>;
}

export default function LogExpired() {
  const uzka = useUzkaObrazovka();
  const user = getCurrentUser();
  const role = user?.role || "";
  const canEdit = role === "owner" || role === "manager";
  const canDelete = role === "owner";
  const canForcePhase2 = role === "owner";

  const [year, setYear] = useState(getDefaultYear());
  const [month, setMonth] = useState(getDefaultMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionPending, setActionPending] = useState({}); // { rowIndex: true } during update
  const [search, setSearch] = useState("");
  const [filterSchvaleno, setFilterSchvaleno] = useState(""); // "" / ANO / NE
  const [filterPilot, setFilterPilot] = useState(""); // "" / automatizace / manual
  const [filterPriciteno, setFilterPriciteno] = useState(""); // "" / ANO (jen pro KPI dlaždici Přepočteno)
  const [selected, setSelected] = useState(new Set()); // rowIndex set
  // Confirm dialog states
  const [bulkPending, setBulkPending] = useState(null); // { schvaleno, count }
  const [deletePending, setDeletePending] = useState(null); // { rowIndex, jmeno }
  const [togglePending, setTogglePending] = useState(null); // { entry, next }
  const [createOpen, setCreateOpen] = useState(false);
  const [forcePhase2Pending, setForcePhase2Pending] = useState(false);
  const [phase2Result, setPhase2Result] = useState(null);
  // Klient seznam pro fuzzy autocomplete — sdileny hook s sessionStorage cache + retry.
  // Pri mount: instant z cache (pokud < 10 min) + fresh fetch na pozadi.
  const klientOptions = useClientsList();

  const load = async ({ forceRefresh = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      // Sdílený cache (lib/logExpiredCache.js) — pokud Clients.jsx udělal prewarm,
      // navigation na /clients/log-expired je instantní (filter v paměti).
      const entries = await fetchLogExpired({ year, month, forceRefresh });
      setData({ ok: true, year, month, count: entries.length, entries });
      setSelected(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const entries = data?.entries || [];

  // Lokálně filtrované entries (search + Schváleno + Pilot)
  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterSchvaleno && e.schvaleno !== filterSchvaleno) return false;
      if (filterPilot === "automatizace" && e.pilot.toLowerCase() !== "automatizace") return false;
      if (filterPilot === "manual" && e.pilot.toLowerCase() === "automatizace") return false;
      if (filterPriciteno === "ANO" && e.priciteno !== "ANO") return false;
      if (search) {
        const q = search.toLowerCase();
        if (!e.jmeno.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [entries, filterSchvaleno, filterPilot, filterPriciteno, search]);

  // KPI
  const kpi = useMemo(() => {
    const total = entries.length;
    const ano = entries.filter((e) => e.schvaleno === "ANO").length;
    const ne = entries.filter((e) => e.schvaleno === "NE").length;
    const priciteno = entries.filter((e) => e.priciteno === "ANO").length;
    return { total, ano, ne, priciteno };
  }, [entries]);

  // Klik na toggle button — otevře confirm dialog s vysvětlením (pokud není 60min bypass aktivní).
  // Bypass je společný pro celou stránku ("log-expired:toggle"), takže admin po prvním potvrzení
  // může klikat libovolně rychle dalších 60 minut bez ptání.
  const askToggleSchvaleno = (entry) => {
    if (!canEdit) return;
    const next = entry.schvaleno === "ANO" ? "NE" : "ANO";
    if (isConfirmBypassed("log-expired:toggle")) {
      executeToggle(entry, next);
      return;
    }
    setTogglePending({ entry, next });
  };

  // Vyclenene toggle flow — volaji jak bypass, tak confirm dialog.
  const executeToggle = async (entry, next) => {
    setActionPending((s) => ({ ...s, [entry.rowIndex]: true }));
    try {
      const r = await apiFetch("/api/log-expired", {
        method: "POST",
        body: JSON.stringify({
          action: "update",
          year,
          rowIndex: entry.rowIndex,
          fields: { schvaleno: next },
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      // Optimistic update + invalidate cache (klient detail uvidi fresh data)
      setData((d) => ({
        ...d,
        entries: d.entries.map((e) =>
          e.rowIndex === entry.rowIndex ? { ...e, schvaleno: next } : e
        ),
      }));
      invalidateLogExpiredCache(year);
    } catch (e) {
      setError(e.message);
    } finally {
      setActionPending((s) => ({ ...s, [entry.rowIndex]: false }));
    }
  };

  const confirmToggle = () => {
    if (!togglePending) return;
    const { entry, next } = togglePending;
    setTogglePending(null);
    executeToggle(entry, next);
  };

  // Bulk update
  const askBulk = (schvaleno) => {
    if (selected.size === 0) return;
    setBulkPending({ schvaleno, count: selected.size });
  };
  const confirmBulk = async () => {
    if (!bulkPending) return;
    const rowIndices = Array.from(selected);
    setBulkPending(null);
    try {
      const r = await apiFetch("/api/log-expired", {
        method: "POST",
        body: JSON.stringify({
          action: "bulkUpdate",
          year,
          rowIndices,
          fields: { schvaleno: bulkPending.schvaleno },
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setSelected(new Set());
      invalidateLogExpiredCache(year);
      load({ forceRefresh: true });
    } catch (e) {
      setError(e.message);
    }
  };

  // Delete
  const askDelete = (entry) => {
    if (!canDelete) return;
    setDeletePending({ rowIndex: entry.rowIndex, jmeno: entry.jmeno, priciteno: entry.priciteno });
  };
  const confirmDelete = async () => {
    if (!deletePending) return;
    const { rowIndex } = deletePending;
    setDeletePending(null);
    try {
      const r = await apiFetch("/api/log-expired", {
        method: "POST",
        body: JSON.stringify({ action: "delete", year, rowIndex }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      invalidateLogExpiredCache(year);
      load({ forceRefresh: true });
    } catch (e) {
      setError(e.message);
    }
  };

  // Přepočítat = aplikuj Phase 2 pro aktuálně zvolený měsíc + rok.
  // Nahrazuje původní /api/automatizace-run (které hodnotí předchozí měsíc).
  // Pro aktuální měsíc nebo budoucí měsíce by cron jinak nikdy nepřičetl.
  const confirmForcePhase2 = async () => {
    setForcePhase2Pending(false);
    setPhase2Result({ loading: true });
    try {
      const r = await apiFetch("/api/log-expired", {
        method: "POST",
        body: JSON.stringify({ action: "recalcMonth", year, month }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setPhase2Result({
        ok: true,
        stats: {
          priciteno: j.applied?.length || 0,
          skipped: j.skipped?.length || 0,
          failed: j.failed?.length || 0,
          appliedDetail: j.applied || [],
          failedDetail: j.failed || [],
        },
      });
      invalidateLogExpiredCache(year);
      load({ forceRefresh: true });
    } catch (e) {
      setPhase2Result({ ok: false, error: e.message });
    }
  };

  const toggleSelect = (rowIndex) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((e) => e.rowIndex)));
  };

  return (
    <Stranka>
      {/* Confirm dialogs */}
      <ConfirmDialog
        open={!!bulkPending}
        title="Hromadná akce"
        description={
          bulkPending
            ? `Opravdu nastavit Schváleno = "${bulkPending.schvaleno}" u ${bulkPending.count} řádků? ${
                bulkPending.schvaleno === "ANO"
                  ? "Tito klienti propadnou. Při přepočtu se propadlé tréninky přičtou k jejich odchozeným v daném měsíci."
                  : "Tito klienti budou odpuštěni. Při přepočtu se nezapočtou (případně se odečtou zpět dříve přičtené)."
              }`
            : ""
        }
        confirmLabel={bulkPending?.schvaleno === "ANO" ? "Schválit propadnutí" : "Odpustit"}
        cancelLabel="Zrušit"
        variant={bulkPending?.schvaleno === "ANO" ? "warning" : "save"}
        onConfirm={confirmBulk}
        onCancel={() => setBulkPending(null)}
      />
      <ConfirmDialog
        open={!!togglePending}
        title={togglePending?.next === "ANO" ? "Schválit propadnutí?" : "Odpustit propadnutí?"}
        description={togglePending && (
          <ToggleDialogBody
            entry={togglePending.entry}
            next={togglePending.next}
            year={year}
          />
        )}
        confirmLabel={togglePending?.next === "ANO" ? "Schválit propadnutí" : "Odpustit"}
        cancelLabel="Zrušit"
        variant={togglePending?.next === "ANO" ? "warning" : "save"}
        onConfirm={confirmToggle}
        onCancel={() => setTogglePending(null)}
        actionKey="log-expired:toggle"
        bypassMinutes={60}
      />
      <ConfirmDialog
        open={!!deletePending}
        title="Smazat řádek?"
        description={
          deletePending
            ? `Opravdu smazat řádek "${deletePending.jmeno}"?${
                deletePending.priciteno === "ANO"
                  ? " POZOR: tento řádek už byl přepočten u klienta. Po smazání zůstane navýšení tréninků, nejdřív označ Schváleno=NE a klikni Přepočítat, pak teprve smaž."
                  : ""
              }`
            : ""
        }
        confirmLabel="Smazat"
        cancelLabel="Zrušit"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeletePending(null)}
      />
      <ConfirmDialog
        open={forcePhase2Pending}
        title={`Přepočítat ${MONTH_NAMES[month - 1]} ${year}?`}
        description={`Projde záznamy Log Expired za ${MONTH_NAMES[month - 1]} ${year} a u klientů se Schváleno=ANO + Přičteno=prázdné přičte propadlé tréninky k odchozeným v Stav ${year}. Klienti se Schváleno=ANO + Přičteno=ANO se přeskočí (nezdvojí se).`}
        confirmLabel="Přepočítat"
        cancelLabel="Zrušit"
        variant="primary"
        onConfirm={confirmForcePhase2}
        onCancel={() => setForcePhase2Pending(false)}
      />
      <CreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        year={year}
        defaultMonth={month}
        klientOptions={klientOptions}
        onCreated={() => { setCreateOpen(false); invalidateLogExpiredCache(year); load({ forceRefresh: true }); }}
      />

      {/* Hlavička. Akce jsou ikony (rozhodnutí z fáze 0), výběr období se
          přesunul do pruhu filtrů — v hlavičce se dva selecty na telefonu
          lámaly pod nadpis a braly celou obrazovku. */}
      <GradientHeader
        title="Propadlé tréninky"
        subtitle="Změna se uloží okamžitě u klienta. Automatický přepočet 1.–3. den v měsíci v 7:00, jindy klikni Přepočítat."
        icon={AlertTriangle}
        loading={loading}
        napoveda="ANO = klient přijde o tréninky, propadlé se přičtou k jeho odchozeným v daném měsíci (default po automatice 1. den měsíce). NE = odpouštíme, tréninky se nezapočítají. Automatický přepočet probíhá 1.-3. den měsíce v 7:00. Pokud změníš později, klikni Přepočítat."
        akce={[
          ...(canEdit ? [{ klic: "pridat", ikona: Plus, popis: "Přidat propadlý trénink ručně", onClick: () => setCreateOpen(true) }] : []),
          ...(canEdit && canForcePhase2 ? [{ klic: "prepocet", ikona: Calculator, popis: "Přepočítat tréninky klientů podle aktuálních ANO/NE", onClick: () => setForcePhase2Pending(true) }] : []),
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: load, disabled: loading },
        ]}
      />

      {error && <Hlaska ton="problem" title="Data se nenačetla" akce={<AkceZnovu onClick={load} />}>{String(error)}</Hlaska>}
      {phase2Result && (
        <div className={`rounded-xl p-3 text-sm ${
          phase2Result.loading ? "border border-sky-200 bg-sky-50 text-sky-800" :
          phase2Result.ok ? (phase2Result.stats?.failed > 0 ? "border border-amber-200 bg-amber-50 text-amber-800" : "border border-emerald-200 bg-emerald-50 text-emerald-800") :
          "border border-rose-200 bg-rose-50 text-rose-800"
        }`}>
          {phase2Result.loading && "Spouštím přepočet…"}
          {phase2Result.ok && (
            <div className="space-y-1">
              <div className="font-semibold">
                Přepočet {MONTH_NAMES[month - 1]} {year} dokončen.
              </div>
              <div>
                Přičteno k tréninkům: {phase2Result.stats?.priciteno ?? 0} klientů
                {phase2Result.stats?.skipped > 0 && `, přeskočeno: ${phase2Result.stats.skipped}`}
                {phase2Result.stats?.failed > 0 && `, selhalo: ${phase2Result.stats.failed}`}.
              </div>
              {phase2Result.stats?.appliedDetail?.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs underline opacity-80">Detail přičtených</summary>
                  <ul className="text-xs mt-1 space-y-0.5 max-h-40 overflow-auto">
                    {phase2Result.stats.appliedDetail.map((a, idx) => (
                      <li key={idx}>{a.jmeno}: {a.before} → {a.after} (+{a.addedPropadle})</li>
                    ))}
                  </ul>
                </details>
              )}
              {phase2Result.stats?.failedDetail?.length > 0 && (
                <details className="mt-1" open>
                  <summary className="cursor-pointer text-xs underline font-semibold">Detail selhaných ({phase2Result.stats.failed})</summary>
                  <ul className="text-xs mt-1 space-y-0.5 max-h-40 overflow-auto">
                    {phase2Result.stats.failedDetail.map((f, idx) => (
                      <li key={idx}>{f.jmeno}: {f.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              <button onClick={() => setPhase2Result(null)} className="text-xs underline opacity-80">zavřít</button>
            </div>
          )}
          {phase2Result.ok === false && <span>Chyba: {phase2Result.error}</span>}
        </div>
      )}

      {/* KPI — klikací dlaždice nastavují filtr níže. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <DlazdiceFiltr popisek="Celkem v měsíci" hodnota={kpi.total} ikona={Users} ton="neutralni"
          info={<InfoTip text="Počet řádků v Log Expired pro vybraný měsíc + rok. Klik = vyčistí všechny filtry." />}
          zapnuto={!filterSchvaleno && !filterPriciteno}
          onClick={() => { setFilterSchvaleno(""); setFilterPriciteno(""); }} />
        <DlazdiceFiltr popisek="Propadnou (ANO)" hodnota={kpi.ano} ikona={AlertTriangle} ton="problem"
          info={<InfoTip text="Klienti se Schváleno=ANO. Tito reálně přijdou o tréninky. Klik = filtr Schváleno=ANO." />}
          zapnuto={filterSchvaleno === "ANO"}
          onClick={() => { setFilterSchvaleno(filterSchvaleno === "ANO" ? "" : "ANO"); setFilterPriciteno(""); }} />
        <DlazdiceFiltr popisek="Odpuštěno (NE)" hodnota={kpi.ne} ikona={CheckCircle2} ton="ok"
          info={<InfoTip text="Klienti se Schváleno=NE. Trénink jim zůstane jako kredit. Klik = filtr Schváleno=NE." />}
          zapnuto={filterSchvaleno === "NE"}
          onClick={() => { setFilterSchvaleno(filterSchvaleno === "NE" ? "" : "NE"); setFilterPriciteno(""); }} />
        <DlazdiceFiltr popisek="Přepočteno" hodnota={kpi.priciteno} ikona={Clock} ton="pozor"
          info={<InfoTip text="Klienti, jejichž propadlé tréninky už byly přičteny k odchozeným v daném měsíci (Přičteno=ANO). Klik = filtr Přepočteno=ANO." />}
          zapnuto={filterPriciteno === "ANO"}
          onClick={() => { setFilterPriciteno(filterPriciteno === "ANO" ? "" : "ANO"); setFilterSchvaleno(""); }} />
      </div>

      {/* Filtry, hromadné akce a seznam. */}
      <Card title={`${MONTH_NAMES[month - 1]} ${year}`} ikona={Filter} lista odznak={`${filtered.length} z ${entries.length}`}>
        <div className="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-5">
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} aria-label="Měsíc" className="field">
            {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} aria-label="Rok" className="field">
            {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={filterSchvaleno} onChange={(e) => setFilterSchvaleno(e.target.value)} aria-label="Rozhodnutí"
            className={`field ${filterSchvaleno ? "border-brand font-semibold text-brand-deep" : ""}`}>
            <option value="">Všechna rozhodnutí</option>
            <option value="ANO">Propadne (ANO)</option>
            <option value="NE">Odpuštěno (NE)</option>
          </select>
          <select value={filterPilot} onChange={(e) => setFilterPilot(e.target.value)} aria-label="Pilot"
            className={`field ${filterPilot ? "border-brand font-semibold text-brand-deep" : ""}`}>
            <option value="">Všechny piloty</option>
            <option value="automatizace">Automatizace</option>
            <option value="manual">Manuální</option>
          </select>
          <input type="text" placeholder="Hledat klienta" value={search} onChange={(e) => setSearch(e.target.value)}
            aria-label="Hledat klienta" className="field col-span-2 lg:col-span-1" />
        </div>

        {canEdit && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button onClick={() => askBulk("ANO")} disabled={selected.size === 0} className="btn btn-secondary btn-sm">
              Schválit propadnutí
            </button>
            <button onClick={() => askBulk("NE")} disabled={selected.size === 0} className="btn btn-secondary btn-sm">
              Odpustit
            </button>
            <button onClick={() => setCreateOpen(true)} className="btn btn-primary btn-sm">
              <Plus className="h-4 w-4" /> Přidat ručně
            </button>
            {canForcePhase2 && (
              <button onClick={() => setForcePhase2Pending(true)} className="btn btn-secondary btn-sm"
                title="Přepočítá tréninky klientů podle aktuálních ANO/NE + pošle Slack zprávu">
                <Calculator className="h-4 w-4" /> Přepočítat
              </button>
            )}
            {selected.size > 0 && <span className="text-xs font-bold text-brand-deep">Vybráno {selected.size}</span>}
          </div>
        )}

        {!canEdit && <div className="mb-2 text-xs font-semibold text-brand-deep">Jen ke čtení (role CSM).</div>}

        {loading && entries.length === 0 ? (
          <SkeletonRows rows={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon={AlertTriangle}
            title={entries.length === 0 ? `Žádné záznamy za ${MONTH_NAMES[month - 1]} ${year}` : "Nic neodpovídá filtru"}
            hint={entries.length === 0 ? "V tomto měsíci zatím nikdo nepropadl." : "Zkus jiné období nebo zruš filtr."} />
        ) : uzka ? (
          /* Telefon: z osmi sloupců se vešly dva. Karta má jméno na vlastním
             řádku, pod ním důvod a štítky, rozhodnutí je tlačítko přes celou šířku. */
          <div className="space-y-2">
            {filtered.map((e) => (
              <div key={e.rowIndex} className="polozka">
                <div className="flex items-start gap-2">
                  {canEdit && (
                    <input type="checkbox" checked={selected.has(e.rowIndex)} onChange={() => toggleSelect(e.rowIndex)}
                      aria-label={`Vybrat ${e.jmeno}`} className="mt-1 h-5 w-5 shrink-0 accent-brand" />
                  )}
                  <div className="min-w-0 flex-1">
                    <ZalozkaLink to={`/clients?search=${encodeURIComponent(e.jmeno || "")}&open=1`} title={e.jmeno}
                      className="text-[15px] font-bold leading-snug text-slate-900 hover:text-brand-deep hover:underline demo-sensitive">
                      {e.jmeno}
                    </ZalozkaLink>
                    {e.duvod && <div className="mt-0.5 text-[12.5px] leading-snug text-slate-600">{e.duvod}</div>}
                  </div>
                  <span className="shrink-0 text-lg font-black tabular-nums text-rose-600">{e.propadle}</span>
                  {canDelete && (
                    <button onClick={() => askDelete(e)} title="Smazat řádek" aria-label="Smazat řádek"
                      className="btn-ikona shrink-0 border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <PricitenoPill schvaleno={e.schvaleno} priciteno={e.priciteno} />
                  <PilotPill value={e.pilot} />
                </div>
                {canEdit ? (
                  <button onClick={() => askToggleSchvaleno(e)} disabled={!!actionPending[e.rowIndex]}
                    title="Klepnutím přepneš ANO ↔ NE"
                    className={`mt-2 flex min-h-[44px] w-full items-center justify-center rounded-xl text-sm font-bold transition disabled:opacity-50 ${
                      e.schvaleno === "ANO" ? "bg-rose-100 text-rose-700"
                        : e.schvaleno === "NE" ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-600"
                    }`}>
                    {e.schvaleno === "ANO" ? "Propadne (ANO)" : e.schvaleno === "NE" ? "Odpuštěno (NE)" : "Nerozhodnuto"}
                  </button>
                ) : (
                  <div className="mt-2"><StatusPill value={e.schvaleno} /></div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="-mx-4 overflow-x-auto sm:mx-0">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-500">
                  {canEdit && (
                    <th className="w-10 px-2 py-2">
                      <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length}
                        onChange={toggleSelectAll} aria-label="Vybrat vše" className="h-4 w-4 accent-brand" />
                    </th>
                  )}
                  <th className="px-2 py-2 font-medium">Klient</th>
                  <th className="px-2 py-2 font-medium">Důvod</th>
                  <th className="px-2 py-2 font-medium">Propadlých</th>
                  <th className="px-2 py-2 font-medium">Schváleno</th>
                  <th className="px-2 py-2 font-medium">Přepočet</th>
                  <th className="px-2 py-2 font-medium">Pilot</th>
                  <th className="px-2 py-2 font-medium" aria-label="Akce" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.rowIndex} className="border-b border-slate-100 hover:bg-brand-50/50">
                    {canEdit && (
                      <td className="px-2 py-2">
                        <input type="checkbox" checked={selected.has(e.rowIndex)} onChange={() => toggleSelect(e.rowIndex)}
                          aria-label={`Vybrat ${e.jmeno}`} className="h-4 w-4 accent-brand" />
                      </td>
                    )}
                    <td className="px-2 py-2 font-medium" title={e.duvod}>
                      {/* Klikatelné jméno → /clients?search=...&open=1 otevře detail klienta v modalu.
                          7.7.2026 feedback #28: nemá se ztratit rozdělaná stránka. 12.8.2026: místo
                          okna prohlížeče se otevře záložka uvnitř adminu (Ctrl pořád dělá okno). */}
                      <ZalozkaLink to={`/clients?search=${encodeURIComponent(e.jmeno || "")}&open=1`} title={e.jmeno}
                        className="text-slate-900 transition-colors hover:text-brand-deep hover:underline demo-sensitive">
                        {e.jmeno}
                      </ZalozkaLink>
                    </td>
                    <td className="max-w-md px-2 py-2 text-slate-600" title={e.duvod || "—"}>
                      <span className="line-clamp-2">{e.duvod || "—"}</span>
                    </td>
                    <td className="px-2 py-2 font-bold tabular-nums text-rose-600">{e.propadle}</td>
                    <td className="px-2 py-2">
                      {canEdit ? (
                        <button onClick={() => askToggleSchvaleno(e)} disabled={!!actionPending[e.rowIndex]}
                          className={`rounded-lg px-2 py-1 text-xs font-medium transition disabled:opacity-50 ${
                            e.schvaleno === "ANO" ? "bg-rose-100 text-rose-700 hover:bg-rose-200"
                              : e.schvaleno === "NE" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                              : "bg-slate-100 text-slate-600"
                          }`}
                          title="Klikni pro přepnutí ANO ↔ NE">
                          {e.schvaleno === "ANO" ? "Propadne (ANO)" : e.schvaleno === "NE" ? "Odpuštěno (NE)" : "—"}
                        </button>
                      ) : (
                        <StatusPill value={e.schvaleno} />
                      )}
                    </td>
                    <td className="px-2 py-2"><PricitenoPill schvaleno={e.schvaleno} priciteno={e.priciteno} /></td>
                    <td className="px-2 py-2"><PilotPill value={e.pilot} /></td>
                    <td className="whitespace-nowrap px-2 py-2">
                      {canDelete && (
                        <button onClick={() => askDelete(e)} title="Smazat řádek" aria-label="Smazat řádek"
                          className="btn-ikona border-rose-200 text-rose-500 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Stranka>
  );
}

// Hezký popis akce v ConfirmDialog při toggle Schváleno.
// Vizuální transition: aktuální stav → nový stav přes barevné badges + šipku.
// Plus lidský popis co se reálně stane + conditional info kdy se přepočítá.
function ToggleDialogBody({ entry, next, year }) {
  const propadleSlovo =
    entry.propadle === 1 ? "trénink" : entry.propadle < 5 ? "tréninky" : "tréninků";
  const today = new Date();
  const dayOfMonth = today.getDate();
  // Auto-přepočet probíhá 1.-3. den v měsíci v 5 UTC. Pokud je dnes 4+ den měsíce
  // nebo už po 7:00 CEST 3. dne, user musí kliknout Přepočítat.
  const isAfterAutoWindow = dayOfMonth > 3;

  return (
    <div className="space-y-3 mt-2">
      {/* Hlavička s klientem a měsícem */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-base font-semibold text-slate-900">{entry.jmeno}</span>
        <span className="text-sm font-medium text-slate-700">{entry.mesic} {year}</span>
        <span className="inline-flex items-center gap-1 text-sm font-bold text-rose-600 tabular-nums">
          {entry.propadle} {propadleSlovo}
        </span>
      </div>

      {/* Vizuální transition: současný stav → nový stav */}
      <div className="flex items-center gap-2">
        <StateBadge state={entry.schvaleno} muted />
        <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <StateBadge state={next} />
      </div>

      {/* Lidský popis akce — co se stane */}
      <div className={`rounded-xl border p-3.5 ${
        next === "ANO"
          ? "bg-rose-50 border-rose-200 text-rose-900"
          : "bg-emerald-50 border-emerald-200 text-emerald-900"
      }`}>
        {next === "ANO" ? (
          <div className="text-sm leading-relaxed">
            <span className="font-semibold">{entry.propadle} {propadleSlovo}</span>{" "}
            se <span className="font-semibold">přičte k odchozeným</span> jako{" "}
            <span className="font-semibold">propadnuté</span> v měsíci{" "}
            <span className="font-semibold">{entry.mesic}</span>. Klient přijde o tyto tréninky.
          </div>
        ) : (
          <div className="text-sm leading-relaxed">
            <span className="font-semibold">{entry.propadle} {propadleSlovo}</span>{" "}
            <span className="font-semibold">NEbude</span> započítáno jako propadnuté v měsíci{" "}
            <span className="font-semibold">{entry.mesic}</span>. Klient o tyto tréninky{" "}
            <span className="font-semibold">nepřijde</span>.
          </div>
        )}
      </div>

      {/* Info kdy se to projeví u klienta — různé barvy podle situace */}
      {isAfterAutoWindow ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 flex gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed text-amber-900">
            <div className="font-semibold mb-0.5">Automatický přepočet už proběhl ({dayOfMonth}. den měsíce).</div>
            <div>
              Aby se změna projevila u klienta, klikni v hlavičce na tlačítko{" "}
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-white border border-amber-300 text-amber-800 text-xs font-semibold">Přepočítat</span>.
            </div>
          </div>
        </div>
      ) : (
        <div className="flex gap-2.5 rounded-xl border border-sky-200 bg-sky-50 p-3.5">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-600" />
          <div className="text-sm leading-relaxed text-sky-900">
            <div className="font-semibold mb-0.5">Změna se přepočítá automaticky.</div>
            <div>
              Dnes je {dayOfMonth}. den v měsíci. Auto-přepočet probíhá 1.-3. den v 7:00.
              Pokud chceš okamžitě, klikni v hlavičce na{" "}
              <span className="inline-flex items-center rounded border border-sky-300 bg-white px-1.5 py-0.5 text-xs font-semibold text-sky-800">Přepočítat</span>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Barevný badge zobrazující stav Schváleno (ANO/NE) v dialogu.
// `muted` = zšeřelý (= aktuální stav), bez muted = výrazný (= nový stav).
function StateBadge({ state, muted = false }) {
  if (state === "ANO") {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
        muted ? "bg-rose-50 text-rose-400 border border-rose-100" : "bg-rose-500 text-white shadow-sm"
      }`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80"></span>
        Propadne (ANO)
      </span>
    );
  }
  if (state === "NE") {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
        muted ? "bg-emerald-50 text-emerald-400 border border-emerald-100" : "bg-emerald-600 text-white shadow-sm"
      }`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80"></span>
        Odpuštěno (NE)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-500">
      —
    </span>
  );
}

function CreateModal({ open, onClose, year, defaultMonth, onCreated, klientOptions = [] }) {
  // Default = aktualni mesic (kdy user vlozi rucni zapis). NE predchozi mesic z page filtru,
  // protoze user typicky pridava propadli pro aktualni mesic ktery prave bezi.
  const currentMonth = new Date().getMonth() + 1; // 1-indexed
  const [jmeno, setJmeno] = useState("");
  const [mesic, setMesic] = useState(currentMonth);
  const [propadle, setPropadle] = useState(1);
  const [duvod, setDuvod] = useState("");
  const [schvaleno, setSchvaleno] = useState("ANO");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setJmeno(""); setMesic(new Date().getMonth() + 1); setPropadle(1); setDuvod("");
      setSchvaleno("ANO"); setErr(null);
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!jmeno.trim()) { setErr("Jméno je povinné"); return; }
    setSaving(true);
    setErr(null);
    try {
      const r = await apiFetch("/api/log-expired", {
        method: "POST",
        body: JSON.stringify({
          action: "create",
          year,
          entry: {
            jmeno: jmeno.trim(),
            mesic: MONTH_NAMES[mesic - 1],
            propadle: Number(propadle),
            duvod: duvod.trim() || `Manuálně přidáno`,
            schvaleno,
          },
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      // Pokud backend nedokázal přičíst k Stav YYYY (klient nenalezen, sheet chybí atd.),
      // ukaž warning v modalu před zavřením. Schválené=ANO bez phase2.applied = upozorni.
      if (schvaleno === "ANO" && j.phase2 && j.phase2.applied === false && !j.phase2.skipped) {
        setErr(`Záznam uložen, ale auto-přepočet selhal: ${j.phase2.reason || j.phase2.error || "neznámý důvod"}. Klikni "Přepočítat" v hlavičce.`);
        setSaving(false);
        return;
      }
      onCreated();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open onClose={() => { if (!saving) onClose(); }} title="Přidat propadlý trénink ručně" size="sm"
      footer={
        <>
          <button onClick={onClose} disabled={saving} className="btn btn-secondary">Zrušit</button>
          <button onClick={submit} disabled={saving} className="btn btn-primary">{saving ? "Ukládám…" : "Přidat"}</button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Pro klienta, kterého automatika nedetekovala, ale měl by mít propadlé.
        </p>
        <div>
          <div className="label flex items-center gap-1.5">Jméno klienta <InfoTip text={CLIENT_AUTOCOMPLETE_TOOLTIP} /></div>
          <ClientAutocomplete value={jmeno} onChange={setJmeno} options={klientOptions} accentColor="violet"
            placeholder="Začni psát jméno nebo příjmení…" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Měsíc">
            <select value={mesic} onChange={(e) => setMesic(parseInt(e.target.value, 10))} disabled={saving} className="field">
              {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </Field>
          <Field label="Počet propadlých">
            <input type="number" min="1" value={propadle} onChange={(e) => setPropadle(e.target.value)} disabled={saving} className="field" />
          </Field>
        </div>
        <Field label="Důvod" hint="Nepovinné.">
          <input type="text" value={duvod} onChange={(e) => setDuvod(e.target.value)} disabled={saving}
            placeholder="např. Koučink 2T: minimum 6 tréninků/měsíc." className="field" />
        </Field>
        <Field label="Schváleno">
          <select value={schvaleno} onChange={(e) => setSchvaleno(e.target.value)} disabled={saving} className="field">
            <option value="ANO">ANO (klient propadne)</option>
            <option value="NE">NE (odpouštíme)</option>
          </select>
        </Field>
        {err && <Hlaska ton="problem" title="Uložení se nepovedlo">{err}</Hlaska>}
      </div>
    </Modal>
  );
}
